import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import useFetch from 'src/components/leases/useLeaseRecordsFetch';
import { Loader2, X } from 'src/components/common/icons';
import { ITableColumn, ITableConfig, Table } from 'src/components/common/table';
import PagedResultSet from 'src/models/common/PagedResultSet';
import { ILeaseRecordsApiError, LeaseRecordsErrorAlert, parseLeaseRecordsApiError } from '../LeaseRecordsApiError';
import { DateField, Field } from './Master';

interface IProps {
    isOpen: boolean;
    onClose: () => void;
}

interface INotPaidRow {
    rowId: string;
    clientNo?: number;
    prospectNo?: number;
    leaseNo?: number;
    invoiceNo?: string;
    lessor?: string;
    draftAmount?: number;
    amountDue?: number;
    amountPaid?: number;
    invoiceDate?: string;
    dueDate?: string;
    invoiceVoidDate?: string;
    leaseVoidDate?: string;
}

interface IInvoiceAmountDetailRow {
    rowId: string;
    leaseNo?: number;
    invoiceNo?: string;
    invoiceAdd?: string;
    total?: number;
    type?: string;
    tag?: number;
    checkNo?: string;
    checkId?: string;
    void?: string;
    chkInv?: string;
    pbo?: string;
}

interface ILeaseCheckDetailRow {
    rowId: string;
    leaseNo?: number;
    invoiceNo?: string;
    invoiceAdd?: string;
    total?: number;
    checkNo?: string;
    type?: string;
    tag?: number;
    checkId?: string;
}

interface ITransactionListRow {
    rowId: string;
    leaseNo?: number;
    invoiceNo?: string;
    invoiceAdd?: string;
    total?: number;
    type?: string;
    tag?: number;
}

interface ITagItemPayload {
    tag: number;
    type: string;
}

const PAGE_SIZE = 25;
const DETAIL_ROW_COUNT = 12;
const CHECK_ROW_COUNT = 8;
const TRANSACTION_ROW_COUNT = 12;

const formatDateForDisplay = (value?: string): string => {
    if (!value) {
        return '-';
    }

    const parsed = new Date(value);
    if (isNaN(parsed.getTime())) {
        return '-';
    }

    return parsed.toLocaleDateString('en-US');
};

const formatCurrency = (value?: number | string): string => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }

    const parsed = typeof value === 'number' ? value : parseFloat(value);
    if (isNaN(parsed)) {
        return '-';
    }

    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
    }).format(parsed);
};

const formatDateToMMDDYYYY = (value: string): string => {
    if (!value) {
        return '';
    }

    const parts = value.split('-');
    if (parts.length !== 3) {
        return value;
    }

    return `${parts[1]}/${parts[2]}/${parts[0]}`;
};

const createTransactionRows = (): ITransactionListRow[] =>
    Array.from({ length: TRANSACTION_ROW_COUNT }, (_, index) => ({
        rowId: String(index),
    }));

const toFixedPagedResult = <T extends { rowId: string }>(items: T[], rowCount: number): PagedResultSet<T> => {
    const paged = new PagedResultSet<T>();
    paged.items = items;
    paged.totalRecords = items.length;
    paged.pageNumber = 1;
    paged.pageSize = rowCount;
    return paged;
};

const normalizeVoidValue = (value?: string): string => {
    return (value || '').trim().toUpperCase();
};

const isBlankCheckNumber = (value?: string): boolean => {
    const normalized = (value || '').trim();
    return normalized === '' || normalized === '0';
};

const LeaseholdPayableReconciliationDialog: React.FC<IProps> = ({ isOpen, onClose }) => {
    const { get, post, response } = useFetch();

    const [reportDate, setReportDate] = useState('');
    const [begDueDate, setBegDueDate] = useState('');
    const [endDueDate, setEndDueDate] = useState('');
    const [submittedDates, setSubmittedDates] = useState<{ reportDate: string; begDueDate: string; endDueDate: string } | null>(null);

    const [topRows, setTopRows] = useState<PagedResultSet<INotPaidRow> | undefined>();
    const [pageNumber, setPageNumber] = useState(1);
    const [shouldReload, setShouldReload] = useState(false);
    const [isQuerying, setIsQuerying] = useState(false);
    const topRowsRequestId = useRef(0);
    const invoiceRequestId = useRef(0);
    const checkRequestId = useRef(0);

    const [selectedLeaseNo, setSelectedLeaseNo] = useState<number | null>(null);
    const [invoiceRows, setInvoiceRows] = useState<IInvoiceAmountDetailRow[]>([]);
    const [isInvoiceLoading, setIsInvoiceLoading] = useState(false);

    const [checkRows, setCheckRows] = useState<ILeaseCheckDetailRow[]>([]);
    const [isCheckLoading, setIsCheckLoading] = useState(false);

    const [transactionRows, setTransactionRows] = useState<ITransactionListRow[]>(createTransactionRows());
    const [attachCheckNo, setAttachCheckNo] = useState('');
    const [attachCheckId, setAttachCheckId] = useState('');

    const [isZeroDollarPromptOpen, setIsZeroDollarPromptOpen] = useState(false);
    const [zeroDollarDate, setZeroDollarDate] = useState('');
    const [isCreatingZeroDollar, setIsCreatingZeroDollar] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const [requestError, setRequestError] = useState<ILeaseRecordsApiError | null>(null);

    const topColumns: ITableColumn[] = [
        { name: 'Client No.', mapping: 'clientNo' },
        { name: 'Prospect No.', mapping: 'prospectNo' },
        { name: 'Lease No.', mapping: 'leaseNo' },
        { name: 'Invoice No.', mapping: 'invoiceNo' },
        { name: 'Lessor', mapping: 'lessor' },
        { name: 'Draft Amount', mapping: 'draftAmount', format: formatCurrency },
        { name: 'Amount Due', mapping: 'amountDue', format: formatCurrency },
        { name: 'Amount Paid', mapping: 'amountPaid', format: formatCurrency },
        { name: 'Invoice Date', mapping: 'invoiceDate', format: formatDateForDisplay },
        { name: 'Due Date', mapping: 'dueDate', format: formatDateForDisplay },
        { name: 'Invoice Void Date', mapping: 'invoiceVoidDate', format: formatDateForDisplay },
        { name: 'Lease Void Date', mapping: 'leaseVoidDate', format: formatDateForDisplay },
    ];

    const invoiceColumns: ITableColumn[] = [
        { name: 'Lease No.', mapping: 'leaseNo' },
        { name: 'Invoice No.', mapping: 'invoiceNo' },
        { name: 'Inv. Add', mapping: 'invoiceAdd' },
        { name: 'Amount', mapping: 'total', format: formatCurrency, cellClassName: 'leasehold-payable-reconciliation__amount-cell' },
        { name: 'Type', mapping: 'type' },
        { name: 'Tag', mapping: 'tag' },
        { name: 'Check No.', mapping: 'checkNo' },
        { name: 'Check Id', mapping: 'checkId' },
        { name: 'Void', mapping: 'void' },
        { name: 'Chk Inv', mapping: 'chkInv' },
        { name: 'PBO', mapping: 'pbo' },
    ];

    const checkColumns: ITableColumn[] = [
        { name: 'Lease No.', mapping: 'leaseNo' },
        { name: 'Invoice No.', mapping: 'invoiceNo' },
        { name: 'Inv. Add', mapping: 'invoiceAdd' },
        { name: 'Total', mapping: 'total', format: formatCurrency },
        { name: 'Check No.', mapping: 'checkNo' },
        { name: 'Typ', mapping: 'type' },
        { name: 'Tag', mapping: 'tag' },
        { name: 'Check Id', mapping: 'checkId' },
    ];

    const transactionColumns: ITableColumn[] = [
        { name: 'Lease No.', mapping: 'leaseNo' },
        { name: 'Invoice No.', mapping: 'invoiceNo' },
        { name: 'Inv. Add', mapping: 'invoiceAdd' },
        { name: 'Amount', mapping: 'total', format: formatCurrency, cellClassName: 'leasehold-payable-reconciliation__amount-cell' },
        { name: 'Type', mapping: 'type' },
        { name: 'Tag', mapping: 'tag' },
    ];

    const topTableConfig: ITableConfig = {
        rowCount: PAGE_SIZE,
        pageNumber,
        idField: 'rowId',
        columns: topColumns,
    };

    const invoiceTableConfig: ITableConfig = {
        rowCount: DETAIL_ROW_COUNT,
        pageNumber: 1,
        idField: 'rowId',
        columns: invoiceColumns,
    };

    const checkTableConfig: ITableConfig = {
        rowCount: CHECK_ROW_COUNT,
        pageNumber: 1,
        idField: 'rowId',
        columns: checkColumns,
    };

    const transactionTableConfig: ITableConfig = {
        rowCount: TRANSACTION_ROW_COUNT,
        pageNumber: 1,
        idField: 'rowId',
        columns: transactionColumns,
    };

    const invoiceTableData = useMemo(() => toFixedPagedResult(invoiceRows, DETAIL_ROW_COUNT), [invoiceRows]);
    const checkTableData = useMemo(() => toFixedPagedResult(checkRows, CHECK_ROW_COUNT), [checkRows]);
    const transactionTableData = useMemo(() => toFixedPagedResult(transactionRows, TRANSACTION_ROW_COUNT), [transactionRows]);

    const clearTransactionWorkspace = useCallback((): void => {
        setTransactionRows(createTransactionRows());
        setAttachCheckNo('');
        setAttachCheckId('');
    }, []);

    const loadTopRows = useCallback(async (): Promise<void> => {
        if (!submittedDates) {
            return;
        }

        const requestId = ++topRowsRequestId.current;
        setIsQuerying(true);
        setRequestError(null);
        try {
            const skip = (pageNumber - 1) * PAGE_SIZE;
            const params = new URLSearchParams();
            params.append('skip', skip.toString());
            params.append('top', PAGE_SIZE.toString());
            params.append('reportDate', formatDateToMMDDYYYY(submittedDates.reportDate));
            params.append('begDueDate', formatDateToMMDDYYYY(submittedDates.begDueDate));
            params.append('endDueDate', formatDateToMMDDYYYY(submittedDates.endDueDate));

            const data = await get(`/v1.0/leases/admin/leasehold-payable-reconciliation?${params.toString()}`);
            if (requestId !== topRowsRequestId.current) {
                return;
            }
            if (!response.ok || !data) {
                setRequestError(
                    parseLeaseRecordsApiError(data || response.data || { title: 'Payable reconciliation query failed.' }, response.status)
                );
                setTopRows(undefined);
                return;
            }

            const items: INotPaidRow[] = (data.items || []).map((row: INotPaidRow, index: number) => ({
                ...row,
                rowId: `${row.leaseNo || ''}-${row.invoiceNo || ''}-${skip + index}`,
            }));

            const paged = new PagedResultSet<INotPaidRow>();
            paged.items = items;
            paged.totalRecords = data.totalCount || 0;
            paged.pageNumber = pageNumber;
            paged.pageSize = PAGE_SIZE;
            setTopRows(paged);
        } catch {
            if (requestId !== topRowsRequestId.current) {
                return;
            }
            setRequestError(parseLeaseRecordsApiError({ title: 'Payable reconciliation query failed.' }, 500));
            setTopRows(undefined);
        } finally {
            if (requestId === topRowsRequestId.current) {
                setIsQuerying(false);
            }
        }
    }, [get, pageNumber, response, submittedDates]);

    const loadInvoiceAmountDetails = useCallback(
        async (leaseNo: number): Promise<void> => {
            const requestId = ++invoiceRequestId.current;
            if (!leaseNo) {
                setInvoiceRows([]);
                return;
            }

            setIsInvoiceLoading(true);
            setRequestError(null);
            try {
                const data = await get(`/v1.0/leases/admin/leasehold-payable-reconciliation/invoice-details?leaseNo=${leaseNo}`);
                if (requestId !== invoiceRequestId.current) {
                    return;
                }
                if (!response.ok || !Array.isArray(data)) {
                    setRequestError(
                        parseLeaseRecordsApiError(data || response.data || { title: 'Invoice amount detail query failed.' }, response.status)
                    );
                    setInvoiceRows([]);
                    return;
                }

                const mapped = (data as IInvoiceAmountDetailRow[]).map((row, index) => ({
                    ...row,
                    rowId: `${row.leaseNo || ''}-${row.invoiceNo || ''}-${row.tag || ''}-${index}`,
                }));

                setInvoiceRows(mapped);
            } catch {
                if (requestId !== invoiceRequestId.current) {
                    return;
                }
                setRequestError(parseLeaseRecordsApiError({ title: 'Invoice amount detail query failed.' }, 500));
                setInvoiceRows([]);
            } finally {
                if (requestId === invoiceRequestId.current) {
                    setIsInvoiceLoading(false);
                }
            }
        },
        [get, response]
    );

    const loadLeaseCheckDetails = useCallback(
        async (checkId: number): Promise<void> => {
            const requestId = ++checkRequestId.current;
            if (!checkId) {
                setCheckRows([]);
                return;
            }

            setIsCheckLoading(true);
            setRequestError(null);
            try {
                const data = await get(`/v1.0/leases/admin/leasehold-payable-reconciliation/check-details?checkId=${checkId}`);
                if (requestId !== checkRequestId.current) {
                    return;
                }
                if (!response.ok || !Array.isArray(data)) {
                    setRequestError(
                        parseLeaseRecordsApiError(data || response.data || { title: 'Lease check detail query failed.' }, response.status)
                    );
                    setCheckRows([]);
                    return;
                }

                const mapped = (data as ILeaseCheckDetailRow[]).map((row, index) => ({
                    ...row,
                    rowId: `${row.checkId || ''}-${row.tag || ''}-${index}`,
                }));

                setCheckRows(mapped);
            } catch {
                if (requestId !== checkRequestId.current) {
                    return;
                }
                setRequestError(parseLeaseRecordsApiError({ title: 'Lease check detail query failed.' }, 500));
                setCheckRows([]);
            } finally {
                if (requestId === checkRequestId.current) {
                    setIsCheckLoading(false);
                }
            }
        },
        [get, response]
    );

    useEffect(() => {
        invoiceRequestId.current += 1;
        checkRequestId.current += 1;
        setIsInvoiceLoading(false);
        setIsCheckLoading(false);
        setRequestError(null);
        if (!isOpen) {
            topRowsRequestId.current += 1;
            setIsQuerying(false);
            return;
        }

        topRowsRequestId.current += 1;
        setReportDate('');
        setBegDueDate('');
        setEndDueDate('');
        setZeroDollarDate('');
        setSubmittedDates(null);

        setPageNumber(1);
        setTopRows(undefined);
        setInvoiceRows([]);
        setCheckRows([]);
        setSelectedLeaseNo(null);
        clearTransactionWorkspace();
        setShouldReload(false);
        setIsQuerying(false);
    }, [clearTransactionWorkspace, isOpen]);

    useEffect(
        () => () => {
            topRowsRequestId.current += 1;
            invoiceRequestId.current += 1;
            checkRequestId.current += 1;
        },
        []
    );

    const handleQuery = (): void => {
        if (!reportDate || !begDueDate || !endDueDate) {
            alert('Report Date, Beg. Due Date, and End Due Date are required.');
            return;
        }

        if (begDueDate > endDueDate) {
            alert('Beg. Due Date cannot be after End Due Date.');
            return;
        }

        topRowsRequestId.current += 1;
        invoiceRequestId.current += 1;
        checkRequestId.current += 1;
        setIsInvoiceLoading(false);
        setIsCheckLoading(false);
        setPageNumber(1);
        setTopRows(undefined);
        setSelectedLeaseNo(null);
        setInvoiceRows([]);
        setCheckRows([]);
        clearTransactionWorkspace();
        setSubmittedDates({ reportDate, begDueDate, endDueDate });
        setShouldReload(true);
    };

    useEffect(() => {
        if (!isOpen || !shouldReload) {
            return;
        }

        void loadTopRows();
        setShouldReload(false);
    }, [isOpen, loadTopRows, shouldReload]);

    useEffect((): void | (() => void) => {
        if (!isOpen) {
            return undefined;
        }

        const originalOverflow = document.body.style.overflow;
        const originalPaddingRight = document.body.style.paddingRight;
        const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;

        document.body.style.overflow = 'hidden';
        if (scrollbarWidth > 0) {
            document.body.style.paddingRight = `${scrollbarWidth}px`;
        }

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.paddingRight = originalPaddingRight;
        };
    }, [isOpen]);

    const handleTopRowClick = (id: number | string): void => {
        const selected = topRows?.items.find((row) => row.rowId === id.toString());
        if (!selected || !selected.leaseNo) {
            return;
        }

        setSelectedLeaseNo(selected.leaseNo);
        checkRequestId.current += 1;
        setIsCheckLoading(false);
        setInvoiceRows([]);
        setCheckRows([]);
        clearTransactionWorkspace();
        void loadInvoiceAmountDetails(selected.leaseNo);
    };

    const handleInvoiceRowClick = (id: number | string): void => {
        checkRequestId.current += 1;
        setIsCheckLoading(false);
        setCheckRows([]);
        const selected = invoiceRows.find((row) => row.rowId === id.toString());
        if (!selected?.checkId) {
            setCheckRows([]);
            return;
        }

        const parsedCheckId = parseInt(selected.checkId, 10);
        if (isNaN(parsedCheckId)) {
            setCheckRows([]);
            return;
        }

        void loadLeaseCheckDetails(parsedCheckId);
    };

    const addDetailToTransactionList = (row: IInvoiceAmountDetailRow): void => {
        setTransactionRows((previous) => {
            const next = [...previous];
            const blankIndex = next.findIndex((item) => !item.leaseNo && !item.invoiceNo && !item.invoiceAdd && !item.tag && !item.type);

            if (blankIndex < 0) {
                alert('Transaction List is full. Clear Transaction List and try again.');
                return previous;
            }

            next[blankIndex] = {
                rowId: next[blankIndex].rowId,
                leaseNo: row.leaseNo,
                invoiceNo: row.invoiceNo,
                invoiceAdd: row.invoiceAdd,
                total: row.total,
                type: row.type,
                tag: row.tag,
            };

            return next;
        });
    };

    const handleInvoiceRowDoubleClick = (id: number | string): void => {
        const selected = invoiceRows.find((row) => row.rowId === id.toString());
        if (!selected) {
            return;
        }

        const isVoided = normalizeVoidValue(selected.void) === 'Y' || normalizeVoidValue(selected.void) === 'YES';
        if (isBlankCheckNumber(selected.checkNo) || isVoided) {
            addDetailToTransactionList(selected);
            return;
        }

        setAttachCheckNo((selected.checkNo || '').trim());
        setAttachCheckId((selected.checkId || '').trim());
    };

    const handleOpenZeroDollarPrompt = (): void => {
        const now = new Date();
        setZeroDollarDate(`${now.getFullYear()}-12-31`);
        setIsZeroDollarPromptOpen(true);
    };

    const handleCreateZeroDollarCheck = async (): Promise<void> => {
        if (!zeroDollarDate) {
            alert('Check date is required.');
            return;
        }

        setIsCreatingZeroDollar(true);
        setRequestError(null);
        try {
            const data = await post('/v1.0/leases/admin/leasehold-payable-reconciliation/zero-dollar-check', {
                checkDate: zeroDollarDate,
            });

            if (!response.ok || !data) {
                setRequestError(parseLeaseRecordsApiError(data || response.data || { title: 'Zero-dollar check creation failed.' }, response.status));
                return;
            }

            const checkNo = (data.checkNo || '').toString();
            const checkId = (data.checkId || '').toString();
            if (!checkNo || !checkId) {
                alert('Stored procedure did not return a check number/check id.');
                return;
            }

            setAttachCheckNo(checkNo);
            setAttachCheckId(checkId);
            setIsZeroDollarPromptOpen(false);
        } catch {
            setRequestError(parseLeaseRecordsApiError({ title: 'Zero-dollar check creation failed.' }, 500));
        } finally {
            setIsCreatingZeroDollar(false);
        }
    };

    const handleProcessTransactions = async (): Promise<void> => {
        const normalizedCheckNo = attachCheckNo.trim();
        const normalizedCheckId = attachCheckId.trim();
        if (!normalizedCheckNo || !normalizedCheckId) {
            alert('Attach to Check number fields are required.');
            return;
        }

        const tagItems: ITagItemPayload[] = transactionRows
            .filter((row) => row.tag !== undefined && row.tag !== null && !!(row.type || '').trim())
            .map((row) => ({
                tag: row.tag as number,
                type: (row.type || '').trim(),
            }));

        if (!tagItems.length) {
            alert('Transaction List has no records to process.');
            return;
        }

        setIsProcessing(true);
        setRequestError(null);
        try {
            const data = await post('/v1.0/leases/admin/leasehold-payable-reconciliation/process', {
                checkNo: normalizedCheckNo,
                checkId: normalizedCheckId,
                tagList: tagItems,
            });

            if (!response.ok) {
                setRequestError(parseLeaseRecordsApiError(data || response.data || { title: 'Payable cleanup processing failed.' }, response.status));
                return;
            }

            clearTransactionWorkspace();
            setCheckRows([]);
            if (selectedLeaseNo) {
                await loadInvoiceAmountDetails(selectedLeaseNo);
            }
            setShouldReload(true);
        } catch {
            setRequestError(parseLeaseRecordsApiError({ title: 'Payable cleanup processing failed.' }, 500));
        } finally {
            setIsProcessing(false);
        }
    };

    if (!isOpen) {
        return null;
    }

    return (
        <div style={dialogStyles.overlay} onClick={onClose}>
            <div style={dialogStyles.content} onClick={(event) => event.stopPropagation()}>
                <div style={dialogStyles.header}>
                    <h3 style={dialogStyles.title}>Leasehold Payable Reconciliation Utility</h3>
                    <button onClick={onClose} style={dialogStyles.closeButton} aria-label="Close payable reconciliation dialog">
                        <X style={{ width: '1rem', height: '1rem' }} />
                    </button>
                </div>

                <div style={dialogStyles.body}>
                    <LeaseRecordsErrorAlert error={requestError} onDismiss={() => setRequestError(null)} />
                    <div style={dialogStyles.fieldsGrid}>
                        <DateField label="Report Date" value={reportDate} editable={true} onChange={(value) => setReportDate(value || '')} />
                        <DateField label="Beg. Due Date" value={begDueDate} editable={true} onChange={(value) => setBegDueDate(value || '')} />
                        <DateField label="End Due Date" value={endDueDate} editable={true} onChange={(value) => setEndDueDate(value || '')} />
                    </div>

                    <div style={dialogStyles.actionRow}>
                        <button onClick={handleQuery} style={dialogStyles.secondaryButton} disabled={isQuerying || isProcessing}>
                            <div style={dialogStyles.buttonInner}>
                                {isQuerying && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                <span>{isQuerying ? 'Querying...' : 'Query'}</span>
                            </div>
                        </button>
                    </div>

                    <div style={dialogStyles.panel}>
                        <div style={dialogStyles.panelTitle}>Invoices - Not Paid in Full</div>
                        <div
                            className="search-leases__table-wrapper leasehold-payable-reconciliation__top-table"
                            style={dialogStyles.topTableSection}
                        >
                            <Table
                                isLoading={isQuerying}
                                tableConfig={topTableConfig}
                                data={topRows}
                                enablePaging={true}
                                onPageChange={(nextPage) => {
                                    topRowsRequestId.current += 1;
                                    setPageNumber(nextPage);
                                    setShouldReload(true);
                                }}
                                onRowClick={handleTopRowClick}
                            />
                        </div>
                    </div>

                    <div style={dialogStyles.middleGrid}>
                        <div style={dialogStyles.panel}>
                            <div style={dialogStyles.panelTitle}>Invoice Amount Details</div>
                            <div style={dialogStyles.detailHint}>Click row to load Lease Check Details. Double-click row to add/attach.</div>
                            <div
                                className="search-leases__table-wrapper leasehold-payable-reconciliation__invoice-table"
                                style={dialogStyles.invoiceTableSection}
                            >
                                <Table
                                    isLoading={isInvoiceLoading}
                                    tableConfig={invoiceTableConfig}
                                    data={invoiceTableData}
                                    onRowClick={handleInvoiceRowClick}
                                    onRowDoubleClick={handleInvoiceRowDoubleClick}
                                />
                            </div>
                        </div>

                        <div style={dialogStyles.panel}>
                            <div style={dialogStyles.panelTitle}>Transaction List</div>
                            <div
                                className="search-leases__table-wrapper leasehold-payable-reconciliation__transaction-table"
                                style={dialogStyles.transactionTableSection}
                            >
                                <Table tableConfig={transactionTableConfig} data={transactionTableData} />
                            </div>

                            <div style={dialogStyles.attachSection}>
                                <Field
                                    label="Attach to Check No."
                                    value={attachCheckNo}
                                    editable={false}
                                    onChange={() => {
                                        return;
                                    }}
                                />
                                <Field
                                    label="Attach to Check Id"
                                    value={attachCheckId}
                                    editable={false}
                                    onChange={() => {
                                        return;
                                    }}
                                />
                            </div>

                            <div style={dialogStyles.rightActionColumn}>
                                <button
                                    onClick={clearTransactionWorkspace}
                                    style={dialogStyles.secondaryButton}
                                    disabled={isProcessing || isCreatingZeroDollar}
                                >
                                    Clear Transaction List
                                </button>
                                <button
                                    onClick={handleOpenZeroDollarPrompt}
                                    style={dialogStyles.secondaryButton}
                                    disabled={isProcessing || isCreatingZeroDollar}
                                >
                                    Add Zero Dollar Check
                                </button>
                                <button
                                    onClick={() => void handleProcessTransactions()}
                                    style={dialogStyles.primaryButton}
                                    disabled={isProcessing || isCreatingZeroDollar}
                                >
                                    <div style={dialogStyles.buttonInner}>
                                        {isProcessing && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                        <span>{isProcessing ? 'Processing...' : 'Add Transactions to Check'}</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                    <div style={dialogStyles.panel}>
                        <div style={dialogStyles.panelTitle}>Lease Check Details</div>
                        <div
                            className="search-leases__table-wrapper leasehold-payable-reconciliation__check-table"
                            style={dialogStyles.bottomTableSection}
                        >
                            <Table isLoading={isCheckLoading} tableConfig={checkTableConfig} data={checkTableData} />
                        </div>
                    </div>
                </div>

                <div style={dialogStyles.footer}>
                    <button onClick={onClose} style={dialogStyles.secondaryButton} disabled={isProcessing || isCreatingZeroDollar}>
                        Close
                    </button>
                </div>

                {isZeroDollarPromptOpen && (
                    <div style={dialogStyles.promptOverlay} onClick={() => setIsZeroDollarPromptOpen(false)}>
                        <div style={dialogStyles.promptCard} onClick={(event) => event.stopPropagation()}>
                            <h4 style={dialogStyles.promptTitle}>Add Zero Dollar Check</h4>
                            <DateField
                                label="Check Date"
                                value={zeroDollarDate}
                                editable={true}
                                onChange={(value) => setZeroDollarDate(value || '')}
                            />
                            <div style={dialogStyles.promptActions}>
                                <button
                                    onClick={() => setIsZeroDollarPromptOpen(false)}
                                    style={dialogStyles.secondaryButton}
                                    disabled={isCreatingZeroDollar}
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => void handleCreateZeroDollarCheck()}
                                    style={dialogStyles.primaryButton}
                                    disabled={isCreatingZeroDollar}
                                >
                                    <div style={dialogStyles.buttonInner}>
                                        {isCreatingZeroDollar && <Loader2 className="animate-spin" style={{ width: '1rem', height: '1rem' }} />}
                                        <span>{isCreatingZeroDollar ? 'Creating...' : 'Create Check'}</span>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

const dialogStyles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        inset: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.65)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '0.8rem',
        zIndex: 105,
    },
    content: {
        width: 'min(1800px, 99vw)',
        maxHeight: '95vh',
        backgroundColor: '#FFFFFF',
        borderRadius: '0.65rem',
        border: '1px solid #E5E7EB',
        boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.35)',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '0.75rem 1rem',
        borderBottom: '1px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
    },
    title: {
        margin: 0,
        color: '#111827',
        fontSize: '1rem',
        fontWeight: 700,
    },
    closeButton: {
        color: '#6B7280',
        border: 'none',
        backgroundColor: 'transparent',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    body: {
        padding: '0.75rem 0.9rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.6rem',
        overflowY: 'auto',
        overflowX: 'hidden',
        minHeight: 0,
        flex: 1,
    },
    fieldsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
        gap: '0.6rem',
    },
    actionRow: {
        display: 'flex',
        justifyContent: 'flex-end',
    },
    panel: {
        border: '1px solid #D1D5DB',
        borderRadius: '0.45rem',
        backgroundColor: '#FFFFFF',
        padding: '0.45rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.35rem',
        minHeight: 0,
    },
    panelTitle: {
        fontSize: '0.88rem',
        fontWeight: 700,
        color: '#1F2937',
    },
    detailHint: {
        fontSize: '0.78rem',
        color: '#4B5563',
    },
    topTableSection: {
        height: 'clamp(150px, 20vh, 240px)',
        minHeight: '150px',
    },
    middleGrid: {
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1.45fr) minmax(0, 1fr)',
        gap: '0.6rem',
        minHeight: 0,
        alignItems: 'start',
    },
    invoiceTableSection: {
        height: 'clamp(150px, 20vh, 240px)',
        minHeight: '150px',
    },
    transactionTableSection: {
        height: 'clamp(120px, 14vh, 180px)',
        minHeight: '120px',
    },
    bottomTableSection: {
        height: 'clamp(110px, 13vh, 165px)',
        minHeight: '110px',
    },
    attachSection: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
        gap: '0.5rem',
    },
    rightActionColumn: {
        display: 'grid',
        gap: '0.45rem',
        justifyItems: 'stretch',
    },
    footer: {
        borderTop: '1px solid #E5E7EB',
        backgroundColor: '#F9FAFB',
        padding: '0.65rem 1rem',
        display: 'flex',
        justifyContent: 'flex-end',
    },
    primaryButton: {
        border: 'none',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#1D4ED8',
        color: '#FFFFFF',
        cursor: 'pointer',
        fontWeight: 600,
    },
    secondaryButton: {
        border: '1px solid #D1D5DB',
        borderRadius: '0.4rem',
        padding: '0.45rem 0.9rem',
        backgroundColor: '#FFFFFF',
        color: '#111827',
        cursor: 'pointer',
        fontWeight: 600,
    },
    buttonInner: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '0.45rem',
    },
    promptOverlay: {
        position: 'absolute',
        inset: 0,
        zIndex: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.35)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        padding: '1rem',
    },
    promptCard: {
        position: 'relative',
        zIndex: 1,
        width: 'min(420px, 92vw)',
        backgroundColor: '#FFFFFF',
        border: '1px solid #D1D5DB',
        borderRadius: '0.5rem',
        boxShadow: '0 14px 32px rgba(0, 0, 0, 0.24)',
        padding: '0.85rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.7rem',
    },
    promptTitle: {
        margin: 0,
        fontSize: '0.95rem',
        color: '#111827',
        fontWeight: 700,
    },
    promptActions: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '0.55rem',
    },
};

export default LeaseholdPayableReconciliationDialog;



// WEBPACK FOOTER //
// ./src/components/leases/sections/LeaseholdPayableReconciliationDialog.tsx